import React, { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { PERIOD_OPTIONS } from "./consts";
import { TimespanPickerReturn } from "./hooks";
import { PeriodOptionType } from "./types";
import { isMobile } from "../../../utils/responsive";
import OutsideClickHandler from "../../Common/OutsideClickHandler";
import { BaseDateInput } from "../../FormElement/BaseDateInput";
import DropdownSelect from "../../FormElement/DropdownSelect";

import "./styles.scss";

export interface ITimespanPickerProps {
  timespan: TimespanPickerReturn;
}

export const TimespanPicker: React.FC<ITimespanPickerProps> = ({
  timespan,
}) => {
  const { t: _t } = useTranslation();
  const tCommon = (key: string) => _t(`common.dropdown.${key}`);
  const t = (key: string) => _t(`timespanPicker.${key}`);

  const [showPopup, setShowPopup] = useState<boolean>(false);

  // Used to help the popup stay within the screen
  const closestXScreenEdge =
    window.innerWidth -
      (document.querySelector(".timespan-picker")?.getBoundingClientRect()
        .right ?? 0) <
    200
      ? "right"
      : "left";

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    timespan.updateExternalTimespan();
    setShowPopup(false);
  };

  return (
    <div className="timespan-picker">
      <OutsideClickHandler onOutsideClick={handleClosePopup}>
        <div className="date-drop">
          <button className="btn btn-gray-border" onClick={handleOpenPopup}>
            <span className="bold">{tCommon(timespan.period)}</span>
            <div className="date-txt">
              {timespan.internalTimespan.startDate?.format("YYYY-MM-DD")}
              {" - "}
              {timespan.internalTimespan.endDate?.format("YYYY-MM-DD")}
            </div>
            <i className={`icons icon-drop ${showPopup ? "open" : ""}`}>
              &nbsp;
            </i>
          </button>
        </div>
        {showPopup && (
          <div className={`date-panel ${closestXScreenEdge}`}>
            <div className="period-select">
              <div className="period-select-wrap">
                <DropdownSelect
                  fieldLabel={""}
                  value={timespan.period}
                  isTranslation={true}
                  items={PERIOD_OPTIONS}
                  classNameContainer={"sort-dropdown"}
                  onChange={(value: string) => {
                    timespan.changePeriod(value as PeriodOptionType);
                  }}
                />
                <button className="btn btn-blue" onClick={handleClosePopup}>
                  {t("apply")}
                </button>
              </div>
              <div
                className={`date-input-wrap ${
                  timespan.period !== "custom" ? "disabled" : ""
                }`}
              >
                <BaseDateInput
                  disabled={timespan.period !== "custom"}
                  fieldLabel={""}
                  value={timespan.internalTimespan.startDate?.toDate() ?? null}
                  placeholder={""}
                  minDate={timespan.minDate}
                  maxDate={timespan.internalTimespan.endDate?.toDate()}
                  onChange={(newDate) => {
                    timespan.changeStartDate(newDate as Date);
                  }}
                />
                <span className="to">~</span>
                <BaseDateInput
                  disabled={timespan.period !== "custom"}
                  fieldLabel={""}
                  value={timespan.internalTimespan.endDate?.toDate() ?? null}
                  placeholder={""}
                  minDate={
                    timespan.internalTimespan.startDate?.toDate()
                      ? new Date(timespan.internalTimespan.startDate.toDate())
                      : undefined
                  }
                  onChange={(newDate) => {
                    timespan.changeEndDate(newDate as Date);
                  }}
                />
              </div>
            </div>
            <div
              className={`custom-timespan-select ${
                timespan.period !== "custom" ? "disabled" : ""
              }`}
            >
              <ReactDatePicker
                disabled={timespan.period !== "custom"}
                locale="ja"
                onChange={(dates: [Date | null, Date | null]) => {
                  timespan.changeTimespan(dates[0], dates[1]);
                }}
                minDate={new Date(timespan.minDate)}
                selectsRange
                inline
                startDate={timespan.internalTimespan.startDate?.toDate()}
                endDate={timespan.internalTimespan.endDate?.toDate()}
                monthsShown={isMobile() ? 1 : 2}
              />
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};
